import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { MIXPANEL_ACTIONS, mixpanelTracking } from '@/mixPanel/mixPanelActions';

import NewRequestModal from '../components/NewRequestModal/NewRequestModal';
import { RequestDetailsAcceptanceContext } from './requestDetailsAcceptanceContext';

interface NewRequestModalData {
    userId: string;
    userFullName: string;
    userEmail: string;
    startDate: Date;
    endDate: Date;
}

interface CalendarNewRequestContextValue {
    modalData: NewRequestModalData | null;
    openModal: (data: NewRequestModalData) => void;
    closeModal: () => void;
    isModalOpen: boolean;
}

export const CalendarNewRequestContext = createContext<CalendarNewRequestContextValue>({
    modalData: null,
    openModal: () => null,
    closeModal: () => null,
    isModalOpen: false,
});

interface CalendarNewRequestProviderProps {
    children: ReactNode;
}

export const CalendarNewRequestProvider = ({ children }: CalendarNewRequestProviderProps) => {
    const [modalData, setModalData] = useState<NewRequestModalData | null>(null);
    const { triggerCalendarUpdate } = useContext(RequestDetailsAcceptanceContext);

    const openModal = useCallback((data: NewRequestModalData) => {
        mixpanelTracking(MIXPANEL_ACTIONS.SCHEDULE_NEW_REQUEST_OPENED);
        setModalData(data);
    }, []);

    const closeModal = useCallback(() => setModalData(null), []);

    const handleSuccess = useCallback(() => {
        mixpanelTracking(MIXPANEL_ACTIONS.SCHEDULE_NEW_REQUEST_ADDED);
        triggerCalendarUpdate();
        closeModal();
    }, [triggerCalendarUpdate, closeModal]);

    const contextValue = useMemo(
        () => ({
            modalData,
            openModal,
            closeModal,
            isModalOpen: modalData !== null,
        }),
        [modalData, openModal, closeModal]
    );

    return (
        <CalendarNewRequestContext.Provider value={contextValue}>
            {children}
            {modalData && (
                <NewRequestModal
                    userId={modalData.userId}
                    userFullName={modalData.userFullName}
                    userEmail={modalData.userEmail}
                    startDate={modalData.startDate}
                    endDate={modalData.endDate}
                    onClose={closeModal}
                    onSuccess={handleSuccess}
                />
            )}
        </CalendarNewRequestContext.Provider>
    );
};
